import React from "react";

import { IgButton } from "@intelligems/ig-theme/IgButton";
import { IgChip } from "@intelligems/ig-theme/IgChip";
import { IgTextField } from "@intelligems/ig-theme/IgTextField";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import {
  LIVE_RUNS_DEFAULT_LISTEN_WINDOW_MINUTES,
  LIVE_RUNS_MAX_LISTEN_WINDOW_MINUTES,
} from "@intelligems/ig-types/functions/agentic-codegen/live-runs";
import Stack from "@mui/material/Stack";

import { formatRemainingWindow } from "../utils/formatRemainingWindow";
import { clampWindowMinutes } from "./clampWindowMinutes";
import { UseLiveRunsListenResult } from "./useLiveRunsListen";

export interface LiveRunsListenControlProps {
  listen: UseLiveRunsListenResult;
  /** Show a minutes field next to the Listen button (default: off). */
  allowWindowInput?: boolean;
  /** Initial value of the minutes field. */
  defaultWindowMinutes?: number;
  /** Leading copy while listening; "for the next …" is appended. */
  listeningCopy?: string;
  /** Disables the Listen button (a window already open can still be stopped). */
  readOnly?: boolean;
}

/**
 * Presentational listen-window control shared by every live-runs view. Renders
 * the "Listening" chip with a live countdown and a Stop button while a window
 * is open, and a Listen button (optionally with a minutes field) otherwise.
 */
export const LiveRunsListenControl = ({
  listen,
  allowWindowInput = false,
  defaultWindowMinutes = LIVE_RUNS_DEFAULT_LISTEN_WINDOW_MINUTES,
  listeningCopy = "Logging live runs",
  readOnly = false,
}: LiveRunsListenControlProps) => {
  const [minutes, setMinutes] = React.useState(String(defaultWindowMinutes));

  if (listen.isListening) {
    return (
      <Stack direction="row" spacing={1.5} alignItems="center">
        <IgChip size="small" color="success" label="Listening" />
        <IgTypography variant="caption" color="text.secondary">
          {listeningCopy} for the next{" "}
          {formatRemainingWindow(listen.expiresAt, listen.now)}.
        </IgTypography>
        <IgButton
          size="mini"
          variant="outlined"
          loading={listen.isToggling}
          onClick={listen.close}
        >
          Stop
        </IgButton>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={1} alignItems="center">
      {allowWindowInput ? (
        <>
          <IgTypography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            Log live runs for
          </IgTypography>
          <IgTextField
            size="small"
            value={minutes}
            onChange={(e) => setMinutes(e.target.value)}
            sx={{ width: 56 }}
            slotProps={{
              htmlInput: {
                "aria-label": "Listen window in minutes",
                inputMode: "numeric",
              },
            }}
          />
          <IgTypography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap" }}
          >
            min (max {LIVE_RUNS_MAX_LISTEN_WINDOW_MINUTES}).
          </IgTypography>
        </>
      ) : null}
      <IgButton
        size="mini"
        variant="contained"
        disabled={readOnly}
        loading={listen.isToggling}
        onClick={() =>
          listen.open(
            allowWindowInput ? clampWindowMinutes(minutes) : undefined
          )
        }
      >
        Listen
      </IgButton>
    </Stack>
  );
};
